import React, { useEffect, useState } from 'react';
import {
  Heading,
  NavigationalTab,
  NavigationalTabs,
  ProgressCircular,
} from '@americanexpress/dls-react';
import HotelImageCollage from '../common/HotelImageCollage';
import HotelDescription from '../common/HotelDescription';
import HotelCheckInOut from '../common/HotelCheckInOut';
import HotelFacilities from '../common/HotelFacilities';
import HotelRooms from '../common/HotelRooms';
import HotelReviews from '../common/HotelReviews';
import HotelPolicies from '../common/HotelPolicies';
import HotelImportantInformation from '../common/HotelImportantInformation';
import HotelLocationSection from '../common/HotelLocationSection';
import HotelInsiderTip from '../common/HotelInsiderTip';
import HotelProgramBenefits from '../common/HotelProgramBenefits';
import HotelStarRating from '../common/HotelStarRating';

const lxpHotelShop = require('../../mocks/lxpHotelShop');

// LXP has no sentiment data — section is intentionally omitted
const HOTEL_SECTION_ITEMS = [
  { id: 'overview', label: 'Overview' },
  { id: 'location', label: 'Location' },
  { id: 'facilities', label: 'Facilities' },
  { id: 'rooms', label: 'Rooms' },
  { id: 'reviews', label: 'Reviews' },
  { id: 'policies', label: 'Policies' },
];

const extractHotelPhotos = (shopHotel = {}) => {
  const allImages = shopHotel.propertyImages || [];
  const heroImage = allImages.find((img) => img.isHero);
  const heroUrl = heroImage?.large || allImages[0]?.large;
  const galleryUrls = allImages
    .map((img) => img.large)
    .filter(Boolean)
    .filter((url) => url !== heroUrl);

  return [...new Set([heroUrl, ...galleryUrls].filter(Boolean))];
};

const getCoordinateValue = (address, primaryKey, fallbackKeys = []) => {
  const keys = [primaryKey, ...fallbackKeys];

  for (const key of keys) {
    if (typeof address?.[key] === 'number') {
      return address[key];
    }
  }

  return null;
};

const buildHotelLocation = (hotel = {}) => {
  const locationSource = hotel?.geoLocation || hotel?.address;
  const latitude = getCoordinateValue(locationSource, 'latitude', ['lat']);
  const longitude = getCoordinateValue(locationSource, 'longitude', ['long', 'lng', 'lang']);

  const coordinates = typeof latitude === 'number'
    && typeof longitude === 'number'
    ? {
      lat: latitude,
      lng: longitude,
    }
    : null;

  return {
    coordinates,
    addressLines: [
      hotel?.address?.addressLine1,
      hotel?.address?.cityName,
      hotel?.address?.countryName,
    ].filter(Boolean),
    phone: hotel?.phone || null,
  };
};

const getRefundLabel = (isFreeCancellation) => {
  if (isFreeCancellation === true) return 'Refundable';
  if (isFreeCancellation === false) return 'Non-refundable';
  return null;
};

const getRoomSize = (roomType = {}) => {
  if (roomType?.area?.squareMeters) {
    return { value: roomType.area.squareMeters, unit: 'm\u00B2' };
  }

  if (roomType?.area?.squareFeet) {
    return { value: roomType.area.squareFeet, unit: 'ft\u00B2' };
  }

  return null;
};

const buildRoomPricing = (firstRate) => {
  if (!firstRate) {
    return null;
  }

  const pricing = {
    amount: firstRate.pricingInfo?.total ?? null,
    currency: firstRate.pricingInfo?.currency ?? null,
    strikethroughAmount: firstRate.pricingInfo?.totalStrikeThrough ?? null,
    strikethroughCurrency: firstRate.pricingInfo?.currency ?? null,
    refundLabel: getRefundLabel(firstRate.isFreeCancellation),
    freeCancellationUntil: firstRate.cancelPenalties?.[0]?.end ?? null,
    paymentLabel: firstRate.pricingInfo?.paymentModel ?? null,
  };

  return pricing.amount != null && pricing.currency ? pricing : null;
};

const buildRooms = (roomTypes = []) => roomTypes.map((roomType) => {
  const firstRate = roomType?.rates?.[0];

  return {
    id: roomType.id,
    name: roomType.name,
    description: roomType.description,
    amenities: (roomType?.amenities || [])
      .map((amenity) => amenity?.description)
      .filter(Boolean),
    size: getRoomSize(roomType),
    maxOccupancy: roomType.maxOccupancy,
    bedSummary: (firstRate?.bedGroups || [])
      .map((bedGroup) => bedGroup?.description)
      .filter(Boolean)
      .join(', ') || null,
    photos: (roomType.propertyImages || [])
      .filter((img) => img.large || img.medium)
      .map((img) => ({ url: img.large || img.medium, alt: img.caption || roomType.name })),
    pricing: buildRoomPricing(firstRate),
  };
});

const useLxpData = () => {
  const [shopData, setShopData] = useState();
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setShopData({ body: lxpHotelShop });
      setIsLoading(false);
    }, 1000);

    return () => clearTimeout(timeout);
  }, []);

  return { shopData, error: null, isLoading };
};

// eslint-disable-next-line complexity -- page composes many sections by design
const LxpDetailsPage = () => {
  const { shopData, error, isLoading } = useLxpData();

  if (isLoading) {
    return <ProgressCircular />;
  }

  if (error) {
    return (
      <Heading level={1} variant="sans-xlarge-book">
        Unable to load hotel details
      </Heading>
    );
  }

  const hotel = shopData?.body?.hotel || {};

  const hotelName = hotel?.name;
  const starRatingRaw = Number.parseFloat(hotel?.starRating);
  const starRating = starRatingRaw > 0 ? starRatingRaw : null;
  const city = hotel?.address?.cityName;
  const hotelMetaText = [
    city,
  ].filter(Boolean).join(' | ');

  const roomTypes = hotel?.roomTypes || [];
  const photos = extractHotelPhotos(hotel);
  const facilities = (hotel?.amenities || []).map((a) => a.description).filter(Boolean);
  const rooms = buildRooms(roomTypes);
  const location = buildHotelLocation(hotel);

  const hostLanguages = hotel?.hostLanguages
    ? hotel.hostLanguages.split(',').map((l) => l.trim()).filter(Boolean)
    : [];

  const insiderTip = hotel?.clientHotelDecoration
    ?.clientHotelInfo?.marketingInfo?.insiderTip || null;

  const reviewRating = hotel?.userReviews?.rating
    ? Number.parseFloat(hotel.userReviews.rating)
    : null;

  const policies = [
    ...(hotel?.policies || [])
      .filter((p) => p?.description)
      .map((p) => ({ description: p.description })),
    hotel?.noShowPolicy ? { description: hotel.noShowPolicy } : null,
    ...(hotel?.childPolicies || [])
      .filter((p) => p?.description)
      .map((p) => ({ description: p.description })),
  ].filter(Boolean);

  const renovationAndClosures = hotel?.renovationAndClosures || null;

  const programBenefits = (hotel?.clientHotelDecoration?.programBenefits || [])
    .map((pb) => ({
      programCode: pb.programCode,
      programName: pb.programName,
      benefitTypes: (pb.benefits || [])
        .map((b) => b.type)
        .filter(Boolean),
      specialOffer: pb.specialOffer ? {
        offerCategory: pb.specialOffer.offerCategory || null,
        minNights: pb.specialOffer.minNights || null,
        promotionCode: pb.specialOffer.promotionCode || null,
        cardTypes: (pb.specialOffer.cardTypes || []).filter(Boolean),
      } : null,
    }));

  // Combine check-in instructions and special instructions as the important information block
  const importantInfoParts = [
    hotel?.checkIn?.instructions,
    hotel?.checkIn?.specialInstructions,
  ].filter(Boolean);
  const importantInfo = importantInfoParts.length > 0
    ? importantInfoParts.join('<br /><br />')
    : null;

  const checkInText = [hotel?.checkIn?.beginTime, hotel?.checkIn?.endTime]
    .filter(Boolean)
    .join(' \u2013 ') || null;

  return (
    <>
      <Heading level={1} variant="sans-xlarge-book">{hotelName}</Heading>
      <div className="flex flex-align-center margin-1-t">
        <HotelStarRating rating={starRating} />
        {hotelMetaText ? <p className="body margin-0 margin-1-l">{hotelMetaText}</p> : null}
      </div>
      <HotelImageCollage hotelName={hotelName} photos={photos} />
      <NavigationalTabs className="margin-2-t" aria-label="Hotel detail sections">
        {HOTEL_SECTION_ITEMS.map((section, index) => (
          <NavigationalTab
            key={section.id}
            id={`tab-${section.id}`}
            href={`#${section.id}`}
            aria-current={index === 0 ? 'page' : undefined}
          >
            {section.label}
          </NavigationalTab>
        ))}
      </NavigationalTabs>

      <section id="overview" className="margin-4-t" style={{ scrollMarginTop: 'calc(64px + 1rem)' }}>
        <HotelDescription description={hotel?.description} />
        <HotelCheckInOut
          checkInText={checkInText}
          checkOutText={hotel?.checkOut?.time || null}
        />
        {hostLanguages.length > 0 && (
          <div className="margin-4-t">
            <Heading level={3} variant="sans-medium-bold" className="margin-1-b">
              Languages spoken
            </Heading>
            <p className="body">{hostLanguages.join(', ')}</p>
          </div>
        )}
        <HotelInsiderTip tip={insiderTip} />
      </section>

      <HotelLocationSection
        hotelName={hotelName}
        coordinates={location.coordinates}
        addressLines={location.addressLines}
        phone={location.phone}
      />

      {HOTEL_SECTION_ITEMS.filter((s) => s.id !== 'overview' && s.id !== 'location').map((section) => (
        <section key={section.id} id={section.id} className="margin-4-t" style={{ scrollMarginTop: 'calc(64px + 1rem)' }}>
          <Heading level={2} variant="sans-medium-bold">{section.label}</Heading>
          {section.id === 'facilities' && (
            <HotelFacilities facilities={facilities} />
          )}
          {section.id === 'rooms' && (
            <HotelRooms rooms={rooms} />
          )}
          {section.id === 'reviews' && (
            <HotelReviews
              rating={reviewRating}
              ratingOutOf={5}
              reviewCount={hotel?.userReviews?.reviewCount}
              categories={[]}
            />
          )}
          {section.id === 'policies' && (
            <HotelPolicies policies={policies} />
          )}
        </section>
      ))}
      <section className="margin-4-t">
        <HotelImportantInformation content={importantInfo} />
      </section>
      {renovationAndClosures && (
        <section className="margin-4-t">
          <Heading level={2} variant="sans-medium-bold" className="margin-2-b">
            Renovation and closures
          </Heading>
          <p className="body">{renovationAndClosures}</p>
        </section>
      )}
      <HotelProgramBenefits programs={programBenefits} />
    </>
  );
};

export default LxpDetailsPage;
